"use client";

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useSession } from "next-auth/react";
import { useAnalyticsCollect } from "@/hooks/use-analytics-collect";
import { registerTransport, unregisterTransport } from "@/lib/analytics";

type AnalyticsContextValue = {
  sendEvent: (
    eventType: string,
    page?: string,
    metadata?: Record<string, unknown>,
  ) => void;
  status: string;
  analyticsSessionId: string;
  connectionId: string;
};

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { status: sessionStatus } = useSession();
  const collect = useAnalyticsCollect();

  useEffect(() => {
    registerTransport(collect.sendEvent);
    return () => unregisterTransport();
  }, [collect.sendEvent]);

  return (
    <AnalyticsContext.Provider
      value={{
        sendEvent: collect.sendEvent,
        status: sessionStatus === "authenticated" ? "authed" : "anonymous",
        analyticsSessionId: collect.analyticsSessionId,
        connectionId: collect.connectionId,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    return {
      sendEvent: () => {},
      status: "disconnected",
      analyticsSessionId: "",
      connectionId: "",
    };
  }
  return ctx;
}
